import { Moon, Sun, Monitor, Languages, LogOut, UserCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuthStore } from '@/lib/auth/store';
import { ROLE_BADGE, ROLE_LABELS } from '@/lib/auth/rbac';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils/cn';

export function Topbar() {
  const profile = useAuthStore((s) => s.profile);
  const logout = useAuthStore((s) => s.logout);
  const { theme, setTheme, dir, setDir } = useTheme();

  const initials = (profile?.name || profile?.email || 'A')
    .split(/[@\s.]/)[0]
    .slice(0, 2)
    .toUpperCase();

  const cycleTheme = () => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light');
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/80 px-4 backdrop-blur">
      {/* Mobile brand (desktop shows the brand in the sidebar). */}
      <Link to="/" className="flex items-center gap-2 lg:hidden" aria-label="PakGames home">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-bold text-white"
          style={{ background: 'linear-gradient(135deg, #f95959, #ff9a8e)' }}
        >
          PG
        </span>
        <span className="text-sm font-semibold tracking-tight">PakGames</span>
      </Link>

      <div className="flex-1" />

      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDir(dir === 'ltr' ? 'rtl' : 'ltr')}
              aria-label="Toggle direction"
            >
              <Languages className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Direction: {dir.toUpperCase()}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={cycleTheme} aria-label="Toggle theme">
              <ThemeIcon className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Theme: {theme}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-full py-1 pe-2 ps-1 transition-colors hover:bg-accent">
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <span className="hidden text-start sm:block">
              <span className="block max-w-[10rem] truncate text-sm font-medium leading-tight">
                {profile?.name || profile?.email}
              </span>
              {profile?.role ? (
                <span className={cn('rounded px-1 text-[10px] font-medium', ROLE_BADGE[profile.role])}>
                  {ROLE_LABELS[profile.role]}
                </span>
              ) : null}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="truncate">{profile?.name || 'Administrator'}</div>
            <div className="truncate text-xs font-normal text-muted-foreground">{profile?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <UserCircle className="h-4 w-4" /> Profile
            <span className="ms-auto text-[9px] uppercase text-muted-foreground">Soon</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
